import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toggleWatchlist, listWatchlist } from '../services/watchlistService';
import { getContentById, type ContentItem } from '../services/contentService';
import { useAuth } from './AuthContext';

interface WatchlistState {
  watchlist: ContentItem[];
  loading: boolean;
  error: string | null;
  isInWatchlist: (contentId: string) => boolean;
}

interface WatchlistActions {
  loadWatchlist: () => Promise<void>;
  addToWatchlist: (contentId: string) => Promise<void>;
  removeFromWatchlist: (contentId: string) => Promise<void>;
  toggleWatchlistItem: (contentId: string) => Promise<void>;
  clearWatchlist: () => void;
}

interface WatchlistContextValue extends WatchlistState, WatchlistActions {}

const WatchlistContext = createContext<WatchlistContextValue | undefined>(undefined);

export const WatchlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [watchlist, setWatchlist] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadWatchlist = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      const watchlistIds = await listWatchlist(user.uid);
      const watchlistItems = await Promise.all(
        watchlistIds.map(async (item: { contentId: string; title?: string | null }) => {
          try {
            return await getContentById(item.contentId);
          } catch (error) {
            return null;
          }
        })
      );
      
      const validItems = watchlistItems.filter((item: ContentItem | null): item is ContentItem => item !== null);
      setWatchlist(validItems);
    } catch (err) {
      console.error('Error loading watchlist:', err);
      setError('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const addToWatchlist = useCallback(async (contentId: string) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      await toggleWatchlist(user.uid, contentId);
      await loadWatchlist();
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      throw error;
    }
  }, [user, loadWatchlist]);

  const removeFromWatchlist = useCallback(async (contentId: string) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      await toggleWatchlist(user.uid, contentId);
      await loadWatchlist();
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      throw error;
    }
  }, [user, loadWatchlist]);

  const toggleWatchlistItem = useCallback(async (contentId: string) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      await toggleWatchlist(user.uid, contentId);
      await loadWatchlist();
    } catch (error) {
      console.error('Error toggling watchlist item:', error);
      throw error;
    }
  }, [user, loadWatchlist]);

  const isInWatchlistCheck = useCallback((contentId: string): boolean => {
    return watchlist.some(item => item.id === contentId);
  }, [watchlist]);

  const clearWatchlist = useCallback(() => {
    setWatchlist([]);
    setError(null);
  }, []);

  // Load watchlist when user changes
  useEffect(() => {
    if (user) {
      loadWatchlist();
    } else {
      clearWatchlist();
    }
  }, [user, loadWatchlist, clearWatchlist]);

  const value: WatchlistContextValue = {
    watchlist,
    loading,
    error,
    isInWatchlist: isInWatchlistCheck,
    loadWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    toggleWatchlistItem,
    clearWatchlist
  };

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
};

export const useWatchlist = (): WatchlistContextValue => {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlist must be used within WatchlistProvider');
  }
  return context;
};
